import axios from "axios";
import API_CONFIG from "../config/api";

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth token and logging
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log requests in development
    if (API_CONFIG.ENVIRONMENT === "development") {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (API_CONFIG.ENVIRONMENT === "development") {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Enhanced error handling
    const errorMessage = getErrorMessage(error);

    // Log errors
    console.error("API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: errorMessage,
    });

    // Return standardized error
    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
      originalError: error,
    });
  }
);

// Helper function to get user-friendly error messages
const getErrorMessage = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 400:
        return data?.message || "Invalid request. Please check your input.";
      case 401:
        return "Authentication required. Please log in again.";
      case 403:
        return "You do not have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 422:
        return data?.message || "Validation error. Please check your input.";
      case 500:
        return "Server error. Please try again later.";
      case 503:
        return "Service temporarily unavailable. Please try again later.";
      default:
        return data?.message || `Request failed with status ${status}`;
    }
  } else if (error.request) {
    // Network error
    return "Network error. Please check your connection and try again.";
  } else {
    // Other error
    return error.message || "An unexpected error occurred.";
  }
};

// Retry mechanism for failed requests
const retryRequest = async (config, retryCount = 0) => {
  try {
    return await apiClient(config);
  } catch (error) {
    if (retryCount < API_CONFIG.RETRY_ATTEMPTS && shouldRetry(error)) {
      console.log(
        `Retrying request (${retryCount + 1}/${API_CONFIG.RETRY_ATTEMPTS})`
      );
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * (retryCount + 1))
      ); // Exponential backoff
      return retryRequest(config, retryCount + 1);
    }
    throw error;
  }
};

// Determine if request should be retried
const shouldRetry = (error) => {
  const status = error.response?.status;
  return !status || status >= 500 || status === 408; // Retry on server errors and timeout
};

// Enhanced API methods with retry logic
export const api = {
  get: (url, config = {}) => retryRequest({ ...config, method: "get", url }),
  post: (url, data, config = {}) =>
    retryRequest({ ...config, method: "post", url, data }),
  put: (url, data, config = {}) =>
    retryRequest({ ...config, method: "put", url, data }),
  patch: (url, data, config = {}) =>
    retryRequest({ ...config, method: "patch", url, data }),
  delete: (url, config = {}) =>
    retryRequest({ ...config, method: "delete", url }),
};

export default apiClient;

